import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import MobileFormDialog from '@components/mobile/MobileFormDialog';
import {
  Add,
  Edit,
  Delete,
  Event as EventIcon,
  CalendarMonth,
  List as ListIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { principalAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Returns array of day numbers (null = blank padding), starting Monday
const getMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = firstDay === 0 ? 6 : firstDay - 1; // Monday-first
  const days = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
};

// Format a date string to "YYYY-MM-DD" for comparing
const toDateStr = (year, month, day) => {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const HolidayCalendar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [activeTab, setActiveTab] = useState(0); // 0=Calendar, 1=List

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({ holiday_date: '', name: '', description: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await principalAPI.getHolidays({ year: yearFilter });
      setHolidays(res.data.holidays || []);
    } catch (err) {
      showSnackbar('Failed to load holidays', 'error');
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  // Build a Set of holiday date strings for fast lookup
  const holidayMap = {};
  holidays.forEach(h => { holidayMap[String(h.holiday_date).substring(0, 10)] = h; });

  const handleOpenAdd = (prefillDate = '') => {
    setEditingHoliday(null);
    setFormData({ holiday_date: prefillDate, name: '', description: '' });
    setFormError('');
    setOpenDialog(true);
  };

  const handleOpenEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      holiday_date: holiday.holiday_date,
      name: holiday.name,
      description: holiday.description || ''
    });
    setFormError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (!saving) setOpenDialog(false);
  };

  const handleSave = async () => {
    if (!formData.holiday_date) { setFormError('Please select a date'); return; }
    if (!formData.name.trim()) { setFormError('Holiday name is required'); return; }

    setSaving(true);
    setFormError('');
    try {
      if (editingHoliday) {
        await principalAPI.updateHoliday(editingHoliday.id, formData);
        showSnackbar('Holiday updated successfully');
      } else {
        await principalAPI.createHoliday(formData);
        showSnackbar('Holiday added successfully');
      }
      setOpenDialog(false);
      fetchHolidays();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save holiday';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = (holiday) => {
    setDeleteConfirm({ open: true, id: holiday.id, name: holiday.name });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await principalAPI.deleteHoliday(deleteConfirm.id);
      showSnackbar('Holiday deleted');
      setDeleteConfirm({ open: false, id: null, name: '' });
      fetchHolidays();
    } catch {
      showSnackbar('Failed to delete holiday', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const dateOnly = String(dateStr).substring(0, 10);
    const d = new Date(dateOnly + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  // ---- Calendar Grid View ----
  const CalendarView = () => (
    <Grid container spacing={2}>
      {MONTH_NAMES.map((monthName, monthIdx) => {
        const days = getMonthDays(yearFilter, monthIdx);
        return (
          <Grid item xs={12} sm={6} md={4} key={monthIdx}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                height: '100%'
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ mb: 1, color: 'primary.main', textAlign: 'center', fontSize: '0.85rem' }}
              >
                {monthName} {yearFilter}
              </Typography>

              {/* Day-of-week headers */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ mb: 0.5 }}>
                {DAY_LABELS.map(d => (
                  <Typography
                    key={d}
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: d === 'Sun' ? 'error.main' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.65rem'
                    }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>

              {/* Day cells */}
              <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={0.25}>
                {days.map((day, idx) => {
                  if (!day) return <Box key={idx} />;
                  const dateStr = toDateStr(yearFilter, monthIdx, day);
                  const holiday = holidayMap[dateStr];
                  const isSunday = (idx % 7) === 6; // Sunday is last col (Mon-first)

                  return (
                    <Tooltip
                      key={idx}
                      title={holiday ? holiday.name : ''}
                      arrow
                      disableHoverListener={!holiday}
                    >
                      <Box
                        onClick={() => holiday ? handleOpenEdit(holiday) : handleOpenAdd(dateStr)}
                        sx={{
                          width: '100%',
                          aspectRatio: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          fontSize: '0.7rem',
                          fontWeight: holiday ? 700 : 400,
                          color: holiday
                            ? '#fff'
                            : isSunday
                            ? 'error.main'
                            : 'text.primary',
                          bgcolor: holiday ? 'primary.main' : 'transparent',
                          '&:hover': {
                            bgcolor: holiday ? 'primary.dark' : 'primary.50',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {day}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  // ---- List View ----
  const ListView = () => (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
      {holidays.length === 0 ? (
        <EmptyState
          icon={EventIcon}
          title={`No holidays added for ${yearFilter}`}
          actionLabel="Add First Holiday"
          onAction={() => handleOpenAdd()}
        />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Holiday</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holidays.map(h => (
                <TableRow key={h.id} hover>
                  <TableCell>
                    <Chip
                      label={formatDate(h.holiday_date)}
                      size="small"
                      sx={{ bgcolor: 'primary.50', color: 'primary.dark', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{h.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {h.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(h)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteConfirm(h)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
    <>
        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, bgcolor: '#F8FAFC', p: { xs: 2, sm: 3, md: 4 } }}
        >
          {/* Page Header */}
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} mb={3} gap={2}>
            <Box>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Holiday Calendar
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Manage your school's holiday schedule
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenAdd()}
            >
              Add Holiday
            </Button>
          </Box>

          {/* Controls: View Toggle + Year Filter */}
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={3} gap={2}>
            <Paper sx={{ p: 0.75 }}>
              <ScrollableTabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{
                  minHeight: 0,
                  '& .MuiTabs-flexContainer': { gap: 1 },
                  '& .MuiTab-root': {
                    minHeight: 0,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1,
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }
                  }
                }}
              >
                <Tab icon={<CalendarMonth fontSize="small" />} iconPosition="start" label="Calendar" />
                <Tab icon={<ListIcon fontSize="small" />} iconPosition="start" label="List" />
              </ScrollableTabs>
            </Paper>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={yearFilter}
                label="Year"
                onChange={(e) => setYearFilter(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Summary chip */}
          {!loading && (
            <Box mb={2}>
              <Chip
                icon={<EventIcon />}
                label={`${holidays.length} holiday${holidays.length !== 1 ? 's' : ''} in ${yearFilter}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {/* Content */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            activeTab === 0 ? <CalendarView /> : <ListView />
          )}
        </Box>

      {/* Add / Edit Dialog */}
      <MobileFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
        actions={
          <>
            <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={18} /> : editingHoliday ? 'Update' : 'Add Holiday'}
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Date"
            type="date"
            fullWidth
            required
            value={formData.holiday_date}
            onChange={(e) => setFormData({ ...formData, holiday_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Holiday Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Independence Day"
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Any additional notes..."
          />
          {formError && (
            <Alert severity="error">{formError}</Alert>
          )}
        </Box>
      </MobileFormDialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onClose={() => !deleting && setDeleteConfirm({ open: false, id: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Holiday</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null, name: '' })} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HolidayCalendar;
